import { Link } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import { LEGAL, asDash } from "@/lib/legal";

/**
 * Shared shell for all Legal pages.
 * - Public (no auth required)
 * - Prose styled for readability, not "AI slop"
 * - Consistent header + entity-details footer
 */
export default function LegalLayout({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white co-grain" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400 mb-10 transition-colors"
          data-testid="legal-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Back to MediaOthers
        </Link>

        <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-4">
          <Scale className="w-3 h-3" strokeWidth={1.5} /> Legals
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter mb-3">
          {title}
        </h1>
        {subtitle && (
          <p className="text-zinc-400 text-base leading-relaxed mb-10">{subtitle}</p>
        )}

        <div className="prose prose-invert prose-zinc max-w-none space-y-6 text-zinc-300 leading-relaxed">
          {children}
        </div>

        {/* Entity footer — no physical address by design */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-wider mb-3">
            Legal entity
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500">Company</dt>
              <dd className="text-zinc-200 text-right">{LEGAL.company}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500">Brand</dt>
              <dd className="text-zinc-200 text-right">{LEGAL.brand}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500">Country</dt>
              <dd className="text-zinc-200 text-right">{LEGAL.country}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500">Contact</dt>
              <dd className="text-zinc-200 text-right">
                <a
                  href={`mailto:${LEGAL.email}`}
                  className="text-amber-400 hover:underline"
                  data-testid="legal-contact-email"
                >
                  {LEGAL.email}
                </a>
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500">Chamber (KvK)</dt>
              <dd className="text-zinc-200 text-right font-mono-tech text-xs">{asDash(LEGAL.kvk)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-zinc-500">Establishment nr.</dt>
              <dd className="text-zinc-200 text-right font-mono-tech text-xs">{asDash(LEGAL.establishment)}</dd>
            </div>
            {LEGAL.vat && (
              <div className="flex justify-between gap-4">
                <dt className="text-zinc-500">VAT (BTW)</dt>
                <dd className="text-zinc-200 text-right font-mono-tech text-xs">{LEGAL.vat}</dd>
              </div>
            )}
          </dl>
          <p className="text-[11px] text-zinc-500 leading-relaxed mt-4">
            All correspondence is handled via email — we don&apos;t operate a
            walk-in office. For any legal, privacy or support question please
            write to us at{" "}
            <a href={`mailto:${LEGAL.email}`} className="text-amber-400 hover:underline">
              {LEGAL.email}
            </a>.
          </p>
          <p className="text-[10px] font-mono-tech text-zinc-600 uppercase tracking-wider mt-4">
            Last updated · {LEGAL.effectiveDate}
          </p>
        </div>
      </div>
    </div>
  );
}
